import React from "react";

interface PieChartData {
  label: string;
  value: number;
  color: string;
  percentage: number;
}

interface PieChartProps {
  data: PieChartData[];
  title?: string;
  size?: number;
  showLegend?: boolean;
  showPercentages?: boolean;
  emptyMessage?: string;
}

export function PieChart({
  data,
  title,
  size = 200,
  showLegend = true,
  showPercentages = true,
  emptyMessage = "No data available",
}: PieChartProps) {
  // Filter out zero values and calculate total
  const filteredData = data.filter((item) => item.value > 0);
  const total = filteredData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0 || filteredData.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <svg
            className="w-8 h-8 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        </div>
        <p className="text-gray-500 text-center">{emptyMessage}</p>
      </div>
    );
  }

  // Calculate angles for each slice
  let currentAngle = 0;
  const slices = filteredData.map((item) => {
    const percentage = (item.value / total) * 100;
    const angle = (item.value / total) * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle += angle;

    return {
      ...item,
      percentage,
      startAngle,
      endAngle,
      angle,
    };
  });

  // Create SVG path for each slice
  const createPath = (startAngle: number, endAngle: number, radius: number) => {
    const start = polarToCartesian(0, 0, radius, endAngle);
    const end = polarToCartesian(0, 0, radius, startAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return [
      "M",
      0,
      0,
      "L",
      start.x,
      start.y,
      "A",
      radius,
      radius,
      0,
      largeArcFlag,
      0,
      end.x,
      end.y,
      "Z",
    ].join(" ");
  };

  const polarToCartesian = (
    centerX: number,
    centerY: number,
    radius: number,
    angleInDegrees: number
  ) => {
    const angleInRadians = ((angleInDegrees - 90) * Math.PI) / 180.0;
    return {
      x: centerX + radius * Math.cos(angleInRadians),
      y: centerY + radius * Math.sin(angleInRadians),
    };
  };

  const radius = size / 2 - 10;
  const viewBoxSize = size;

  return (
    <div className="flex flex-col items-center">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}

      <div className="flex items-center space-x-8">
        {/* Pie Chart SVG */}
        <div className="relative">
          <svg
            width={size}
            height={size}
            viewBox={`${-viewBoxSize / 2} ${
              -viewBoxSize / 2
            } ${viewBoxSize} ${viewBoxSize}`}
            className="transform rotate-0"
          >
            {slices.map((slice, index) => (
              <path
                key={index}
                d={createPath(slice.startAngle, slice.endAngle, radius)}
                fill={slice.color}
                stroke="white"
                strokeWidth="2"
                className="hover:opacity-80 transition-opacity cursor-pointer"
                data-title={`${slice.label}: ${
                  slice.value
                } (${slice.percentage.toFixed(1)}%)`}
              />
            ))}
          </svg>
        </div>

        {/* Legend */}
        {showLegend && (
          <div className="space-y-2">
            {slices.map((slice, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: slice.color }}
                />
                <span className="text-sm text-gray-700">
                  {slice.label}
                  {showPercentages && (
                    <span className="text-gray-500 ml-1">
                      ({slice.percentage.toFixed(1)}%)
                    </span>
                  )}
                </span>
                <span className="text-sm font-medium text-gray-900">
                  {slice.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
